import { createOpencodeClient } from "@opencode-ai/sdk/v2/client"
import { OpenCode } from "@opencode-ai/client/promise"
import type { ServerConnection } from "@/context/server"
import { decode64 } from "@/utils/base64"
import { executionSessionApi } from "./server-compat"
import { nativeSessionActive, nativeSessionMessage } from "./native-session-wire"

export function authTokenFromCredentials(input: { username?: string; password: string }) {
  return btoa(`${input.username ?? "opencode"}:${input.password}`)
}

export function authFromToken(token: string | null) {
  const decoded = decode64(token ?? undefined)
  if (!decoded) return
  const separator = decoded.indexOf(":")
  if (separator === -1) return
  return {
    username: decoded.slice(0, separator) || "opencode",
    password: decoded.slice(separator + 1),
  }
}

export function createSdkForServer({
  server,
  ...config
}: Omit<NonNullable<Parameters<typeof createOpencodeClient>[0]>, "baseUrl"> & {
  server: ServerConnection.HttpBase
}) {
  const auth = (() => {
    if (!server.password) return
    return {
      Authorization: `Basic ${authTokenFromCredentials({ username: server.username, password: server.password })}`,
    }
  })()

  return createOpencodeClient({
    ...config,
    headers: {
      ...(config.headers instanceof Headers ? Object.fromEntries(config.headers.entries()) : config.headers),
      ...auth,
    },
    baseUrl: server.url,
  })
}

export function createApiForServer(input: {
  server: ServerConnection.HttpBase
  fetch?: typeof globalThis.fetch
}) {
  const headers = input.server.password
    ? { Authorization: `Basic ${authTokenFromCredentials({ username: input.server.username, password: input.server.password })}` }
    : undefined
  const api = OpenCode.make({
    baseUrl: input.server.url,
    fetch: input.fetch,
    headers,
  })
  return {
    ...api,
    message: { ...api.message, async list(...args: Parameters<typeof api.message.list>) {
      const result = await api.message.list(...args)
      return { ...result, data: result.data.map(nativeSessionMessage) }
    } },
    session: executionSessionApi({ session: { ...api.session,
      async active() { return nativeSessionActive(await api.session.active()) },
      async message(...args: Parameters<typeof api.session.message>) { return nativeSessionMessage(await api.session.message(...args)) },
    }, baseUrl: input.server.url, fetch: input.fetch ?? globalThis.fetch, headers }),
  }
}

export type ServerApi = ReturnType<typeof createApiForServer>
